import { CommonModule } from '@angular/common';
import {
  NgbNav,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavContent,
  NgbScrollSpy,
  NgbScrollSpyFragment,
  NgbDropdownModule,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap';
import { Signal } from '@angular/core';

import {
  ConfigStateService,
  LocalizationService,
  SubscriptionService,
  LocalizationModule,
} from '@abp/ng.core';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit, inject, Injector, ViewChild, computed, OnDestroy } from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { concatMap, of, tap } from 'rxjs';
import { BreadcrumbService } from 'xng-breadcrumb';
import {
  AnnexesListComponent,
  CompanySiteStore,
  GoliathCoreEnumerationTypeStore,
} from '@digiwall/goliath-core';
import { GoliathContactsEnumerationTypeStore, SalesPersonStore } from '@digiwall/goliath-contacts';

import {
  DayBookService,
  SupplierQuoteDto,
  SupplierQuoteService,
  SupplierPurchaseOrderService,
  CreateUpdateSupplierQuoteDto,
  SupplierQuoteDefaultValueDto,
} from '@digiwall/goliath-documents/proxy';
import { SupplierService } from '@digiwall/goliath-contacts/proxy';
import { ClassificationTypeService, CurrencyService, DocRole } from '@digiwall/goliath-core/proxy';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types/enumeration-type.store';
import { DocTemplateService } from '../../base-docs/detail/doc-template-selection/doc-template.service';
import { ReportViewerComponent } from '@odyssey/reporting';
import {
  ChatComponent,
  DeactivatableComponentService,
  DwInputAutoCompleteDirective,
  DwInputRequiredDirective,
  OdysseyOffcanvas,
} from '@digiwall/core';
import { SupplierQuoteLinesEditorComponent } from './supplier-quote-lines/supplier-quote-lines-editor.component';
import { BaseSupplierDocDetail } from '../../base-supplier-docs/base-supplier-doc-detail';
import {
  CounterComponent,
  DropdownMenu,
  DropdownMenuComponent,
  PanelComponent,
} from '@digiwall/ui-kit';
import { DwGridDetailNavigationComponent, DwTabsComponent } from '@digiwall/ag-grid';
import { DwNavLinkDirective } from '@digiwall/core';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { DwKeyboardShortcutDirective } from '@digiwall/core';
import { DwScrollSpyDirective } from '@digiwall/ag-grid';
import { DocConditionsComponent } from '../../base-docs/detail/doc-conditions/doc-conditions.component';
import { DocClassificationsComponent } from '../../base-docs/detail/doc-classifications/doc-classifications.component';
import { DocSupplierAddressesComponent } from '../../base-docs/detail/doc-supplier-addresses/doc-supplier-addresses.component';
import { DocSupplierOthersComponent } from '../../base-docs/detail/doc-supplier-others/doc-supplier-others.component';
import { AsyncPipe } from '@angular/common';
import { SidePanelComponent } from '@digiwall/ui-kit';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { DocSidePanelComponent } from '../../base-docs/detail/doc-ui-components/doc-side-panel/doc-side-panel.component';
import {
  DocNumberTagComponent,
  DocumentCoreCardComponent,
  DocumentSupplierCardComponent,
  SupplierDocumentDateCardComponent,
  SupplierDocumentStatusCardComponent,
} from '../../../components';
import { NgSelectModule } from '@ng-select/ng-select';
import { DocReportPermission } from '@digiwall/goliath-documents/config';
import { SupplierDeliveryMode } from '../../base-supplier-docs/supplier-delivery-mode.enum';
@Component({
  selector: 'document-supplier-quote-detail',
  templateUrl: './supplier-quote-detail.component.html',
  styleUrl: './supplier-quote-detail.component.scss',
  providers: [DocTemplateService, SubscriptionService],
  imports: [
    CommonModule,
    PanelComponent,
    DwTabsComponent,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    DwNavLinkDirective,
    NgbNavContent,
    NgbScrollSpy,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    DwKeyboardShortcutDirective,
    NgbScrollSpyFragment,
    DwScrollSpyDirective,
    SupplierQuoteLinesEditorComponent,
    DocConditionsComponent,
    DocClassificationsComponent,
    DocSupplierAddressesComponent,
    DocSupplierOthersComponent,
    AnnexesListComponent,
    NgbDropdownModule,
    DropdownMenuComponent,
    NgbNavOutlet,
    SidePanelComponent,
    FormsModule,
    DialogBoxComponent,
    ReportViewerComponent,
    AsyncPipe,
    LocalizationModule,
    CounterComponent,
    ChatComponent,
    DwGridDetailNavigationComponent,
    DocSidePanelComponent,
    DocumentCoreCardComponent,
    DocumentSupplierCardComponent,
    SupplierDocumentDateCardComponent,
    SupplierDocumentStatusCardComponent,
    DwInputRequiredDirective,
    DwInputAutoCompleteDirective,
    NgSelectModule,
    DocNumberTagComponent,
  ],
})
export class SupplierQuoteDetailComponent
  extends BaseSupplierDocDetail<
    SupplierQuoteService,
    SupplierQuoteDto,
    CreateUpdateSupplierQuoteDto,
    SupplierQuoteDefaultValueDto
  >
  implements OnInit, OnDestroy
{
  @ViewChild('quoteLineGrid') quoteLineGrid: SupplierQuoteLinesEditorComponent;
  @ViewChild('report') reportViewer: ReportViewerComponent;

  docRole = DocRole.SupplierQuote;

  actions: Signal<DropdownMenu> = computed(() => {
    return {
      groups: [
        this.baseActionsThirdPartyDoc().concat(
          {
            label: '::GeneratePdf',
            icon: 'digi-out-pdf-file',
            action: () => this.generatePdf(this.reportViewer),
          },
          {
            label: '::Copy',
            icon: 'digi-out-copy',
            action: () => this.copy(),
          },
          {
            label: 'Doc::SupplierQuote:ToSupplierPurchaseOrder',
            icon: 'digi-out-add-file',
            action: () => this.toPurchaseOrder(),
            visible: computed(() => !this.isClosed()),
          },
        ),
        [
          {
            label: 'Doc::GenerateDocPrintTemplate',
            icon: 'digi-out-edit-square',
            action: () => this.openDesigner(),
            requiredPolicy: DocReportPermission.Create,
          },
        ],
      ],
    };
  });

  enumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);
  contactEnumerationTypeStore = inject(GoliathContactsEnumerationTypeStore);
  coreEnumerationTypeStore = inject(GoliathCoreEnumerationTypeStore);
  companySiteStore = inject(CompanySiteStore);
  salesPersonStore = inject(SalesPersonStore);
  protected supplierService = inject(SupplierService);
  protected currencyService = inject(CurrencyService);
  totalAmount: number = 0;
  totalAmountVatIncluded: number = 0;

  deliveryMode = SupplierDeliveryMode;

  showSearch: boolean;

  constructor(
    route: ActivatedRoute,
    router: Router,
    subscriptionService: SubscriptionService,
    quoteService: SupplierQuoteService,
    formBuilder: FormBuilder,
    toastService: ToasterService,
    breadcrumbService: BreadcrumbService,
    dayBookService: DayBookService,
    localizationService: LocalizationService,
    classificationTypeService: ClassificationTypeService,
    quoteTemplateService: DocTemplateService,
    injector: Injector,
    thirdPartyService: SupplierService,
    config: ConfigStateService,
    confirmationService: ConfirmationService,
    private purchaseOrderService: SupplierPurchaseOrderService,
    deactivableComponentService: DeactivatableComponentService,
  ) {
    super(
      route,
      router,
      subscriptionService,
      quoteService,
      formBuilder,
      toastService,
      breadcrumbService,
      dayBookService,
      localizationService,
      classificationTypeService,
      quoteTemplateService,
      injector,
      thirdPartyService,
      config,
      confirmationService,
      deactivableComponentService,
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.init();
  }
  override get formModel() {
    return Object.assign({}, super.formModel, {
      supplierQuoteStatusId: [null, Validators.required],
    });
  }

  toPurchaseOrder() {
    this.formStateService.checkDirtyForm(this.form, () => {
      this.purchaseOrderService
        .availabledSupplierQuote(this.entity.id)
        .pipe(
          concatMap(supplierQuote => of(supplierQuote != null)),
          concatMap(canConvert => {
            if (canConvert) {
              return this.confirmationService.warn(
                'Doc::SupplierQuote:ToPurchaseOrder:Confirmation',
                '::AreYouSure',
              );
            }
            return Confirmation.Status.reject;
          }),
          concatMap(confirmationResult => {
            if (confirmationResult == Confirmation.Status.confirm) {
              return this.purchaseOrderService.createFromSupplierQuote(this.entity.id);
            }
            return null;
          }),
          tap(purchaseOrder => {
            if (purchaseOrder) {
              this.toastService.success('Doc::SalePurchaseGenerated');
              this.router.navigate(['../../supplier-purchase-orders', purchaseOrder.id], {
                relativeTo: this.route,
              });
            }
          }),
        )
        .subscribe();
    });
  }

  override getEntityCallback(): void {
    super.getEntityCallback();

    this.quoteLineGrid?.lineGrid?.grid?.grid?.api?.redrawRows();
  }
}
